import { useRouter } from 'next/router';

import { Box, Card, List, Paragraph, Text } from '@zillow/constellation-10';

import { useMultiplexDetails, useMultiplexListingFlow } from '../../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import propertyUtils from '../../../../../utils/propertyUtils';

import { wizardFormSectionContainerStyles, wizardParagraphStyles } from '../../../../../styles/wizard';

import LeaseTermsFormField from '../../../../PostingPath/LeaseTermsFormField';
import RentersInsuranceFormField from '../../../../PostingPath/RentersInsuranceFormField';

type LeaseTermsIntroAndFieldsProps = {
  showMultiplexTips?: boolean;
};

export const LeaseTermsIntroAndFields = ({ showMultiplexTips = false }: LeaseTermsIntroAndFieldsProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const isRoomForRent = propertyUtils.isRoomForRent({
    listing: listingDetails,
  });
  const maintenanceDisplay = isRoomForRent
    ? `Outline renter responsibilities for maintenance, if any.`
    : `Outline tenant responsibilities for maintenance, if any.`;

  const rentersInsurance = !isInMultiplexListingFlow
    ? listingDetails?.rentersInsurance
    : (multiplexData?.rentersInsurance ?? listingDetails?.rentersInsurance);

  const showRentersInsurance = rentersInsurance?.isEligibleForIsRentersInsuranceRequiredQuestion;

  const renderMultiplexTips = showMultiplexTips ? (
    <Card.Root
      tone="neutral"
      elevated={false}
      css={{
        padding: '{spacing.600} {spacing.400}',
        // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
        borderRadius: '{radii.300}',
        width: { base: '100%', xl: '294px' },
        marginBottom: { base: '400', xl: '0' },
        alignSelf: 'flex-start',
        flexShrink: 0,
      }}
    >
      <Card.Body css={{ padding: 'none' }}>
        <Text textStyle="body-bold" css={{ marginBottom: '400', display: 'block' }}>
          Tips for lease descriptions
        </Text>

        <List.Root appearance="bullet">
          <List.Item>Include lease duration.</List.Item>
          <List.Item>
            Add your smoking and parking policies, as well as any other pet policy details not already covered.
          </List.Item>
          <List.Item>{maintenanceDisplay}</List.Item>
        </List.Root>
      </Card.Body>
    </Card.Root>
  ) : null;

  return (
    <>
      <Paragraph css={wizardParagraphStyles}>
        Help renters decide if your property’s a good fit by including key lease details like duration, fees and utility
        costs, and other policies.
      </Paragraph>

      {showMultiplexTips ? (
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', xl: 'row' },
            gap: { base: '0', xl: '400' },
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <LeaseTermsFormField />
          </Box>
          {renderMultiplexTips}
        </Box>
      ) : (
        <LeaseTermsFormField />
      )}

      {showRentersInsurance && <RentersInsuranceFormField />}
    </>
  );
};
